import { useI18n, type Locale } from '../i18n'
import styles from './AppShell.module.css'

/**
 * The EN/FR switch the shell has always claimed to have.
 *
 * The machinery existed from the start — `setLocale` writes `bp.locale` and
 * `initialLocale` reads it on every boot — and nothing called it, so the
 * "explicit stored choice" the specification puts first in its preference order
 * was unreachable except from a browser console (MPAY-32209).
 *
 * Two buttons rather than a `<select>`: with exactly two options a select costs
 * a click to discover what it even contains, and the pair doubles as the
 * indicator of which language is active. `aria-pressed` carries that state to a
 * screen reader, which the visual treatment alone does not.
 */

/** Each language is named IN that language. A user hunting for their own
 *  language scans for the word they themselves would write, so this is the one
 *  list in the product that must not be translated. */
const LANGUAGES: ReadonlyArray<{ readonly code: Locale; readonly name: string }> = [
  { code: 'en', name: 'English' },
  { code: 'fr', name: 'Français' },
]

export function LanguageSwitcher() {
  const { locale, setLocale, t } = useI18n()

  return (
    <div className={styles.langGroup} role="group" aria-label={t('shell.language')}>
      {LANGUAGES.map(({ code, name }) => {
        const active = locale === code
        return (
          <button
            key={code}
            type="button"
            lang={code}
            className={[styles.langButton, active ? styles.langButtonActive : ''].filter(Boolean).join(' ')}
            aria-pressed={active}
            onClick={() => setLocale(code)}
          >
            {name}
          </button>
        )
      })}
    </div>
  )
}
